import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import i18n from '@/i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('urenbuddy_token'));
  const [loading, setLoading] = useState(true);
  const [pendingVerification, setPendingVerification] = useState(null);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
      // Sync language from user profile
      if (res.data.language) {
        i18n.changeLanguage(res.data.language);
        localStorage.setItem('language', res.data.language);
      }
    } catch (err) {
      console.error('Failed to fetch user:', err);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('urenbuddy_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    // Sync language from user profile after login
    if (res.data.user?.language) {
      i18n.changeLanguage(res.data.user.language);
      localStorage.setItem('language', res.data.user.language);
    }
    return res.data;
  };

  const register = async (email, password, bedrijfsnaam) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, bedrijfsnaam });
    // New flow: registration now requires email verification
    if (res.data.requires_verification) {
      setPendingVerification({ email, password, bedrijfsnaam });
      return { ...res.data, requires_verification: true };
    }
    // Fallback for old flow (shouldn't happen with new backend)
    localStorage.setItem('urenbuddy_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const verifyEmail = async (email, code) => {
    const res = await axios.post(`${API}/auth/verify-email`, { email, code });
    localStorage.setItem('urenbuddy_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    setPendingVerification(null);
    return res.data;
  };

  const resendVerification = async (email) => {
    const res = await axios.post(`${API}/auth/resend-verification`, { email });
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('urenbuddy_token');
    setToken(null);
    setUser(null);
    setPendingVerification(null);
  };

  const loginWithGoogle = (tokenData, userData) => {
    localStorage.setItem('urenbuddy_token', tokenData);
    setToken(tokenData);
    setUser(userData);
    // Sync language from user profile
    if (userData?.language) {
      i18n.changeLanguage(userData.language);
      localStorage.setItem('language', userData.language);
    }
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const api = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      verifyEmail,
      resendVerification,
      pendingVerification,
      setPendingVerification,
      logout, 
      loginWithGoogle, 
      updateUser, 
      api, 
      setToken, 
      setUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
